import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Gift, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useShop, FREE_SHIPPING_THRESHOLD } from "@/context/ShopContext";
import { useContent } from "@/context/ContentContext";
import api, { inr } from "@/lib/api";

const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, subtotal, discount, coupon, setCoupon, products } = useShop();
  const { c } = useContent();
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const navigate = useNavigate();

  const effective = subtotal - discount;
  const threshold = Number(c("shipping_free_threshold", String(FREE_SHIPPING_THRESHOLD))) || FREE_SHIPPING_THRESHOLD;
  const progress = Math.min((effective / threshold) * 100, 100);

  const giftEnabled = c("freebie_enabled", "false") === "true";
  const giftThreshold = Number(c("freebie_threshold", "3000")) || 3000;
  const giftProduct = products.find((p) => p.id === c("freebie_product_id", ""));
  const giftUnlocked = !!(giftEnabled && giftProduct && giftProduct.stock > 0 && effective >= giftThreshold);
  const giftLabel = c("freebie_label", "Free gift unlocked");

  const applyCoupon = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      const r = await api.post("/coupons/validate", { code: code.trim() });
      setCoupon(r.data);
      toast.success(`${r.data.code} applied — ${r.data.discount_pct}% off`);
    } catch {
      toast.error("That coupon code isn't valid");
      setCoupon(null);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="flex w-full flex-col bg-cream p-0 sm:max-w-md" data-testid="shopping-cart-drawer">
        <SheetHeader className="border-b border-linen px-6 py-5">
          <SheetTitle className="font-serif text-2xl text-espresso">Your Basket</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cart.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <p className="font-serif text-xl text-espresso">Your basket is empty</p>
              <p className="mt-2 text-sm text-clay/60">Beautiful things take time — start with a mug.</p>
              <button
                onClick={() => {
                  setCartOpen(false);
                  navigate("/shop");
                }}
                className="mt-6 rounded-full bg-espresso px-6 py-3 text-sm font-semibold text-cream transition-colors hover:bg-terra"
              >
                Browse ceramics
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-linen">
              {cart.map((item) => (
                <li key={item.id} className="flex gap-4 py-4" data-testid="cart-item-row">
                  <img src={item.image} alt={item.name} className="h-20 w-20 rounded-xl object-cover" />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-serif text-base leading-tight text-espresso">{item.name}</p>
                      <button onClick={() => removeFromCart(item.id)} aria-label="Remove item" className="text-clay/40 transition-colors hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="mt-0.5 font-mono text-sm text-clay/70">{inr(item.price)}</p>
                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="flex items-center rounded-full border border-linen">
                        <button
                          data-testid="cart-item-qty-minus"
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          className="p-2 text-clay transition-colors hover:text-terra"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-7 text-center font-mono text-sm">{item.qty}</span>
                        <button
                          data-testid="cart-item-qty-plus"
                          onClick={() => updateQty(item.id, Math.min(item.qty + 1, item.stock))}
                          className="p-2 text-clay transition-colors hover:text-terra"
                          aria-label="Increase quantity"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="font-mono text-sm font-semibold text-espresso">{inr(item.price * item.qty)}</span>
                    </div>
                  </div>
                </li>
              ))}
              {giftUnlocked && (
                <li className="flex gap-4 py-4" data-testid="cart-freebie-row">
                  <img src={giftProduct.image} alt={giftProduct.name} className="h-20 w-20 rounded-xl object-cover" />
                  <div className="flex flex-1 flex-col justify-center">
                    <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-sage-light px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-widest text-sage">
                      <Gift className="h-3 w-3" /> {giftLabel}
                    </span>
                    <p className="mt-1.5 font-serif text-base leading-tight text-espresso">{giftProduct.name}</p>
                    <p className="mt-0.5 font-mono text-sm text-sage line-through-none">₹0 · free with your order</p>
                  </div>
                </li>
              )}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-linen bg-sand/60 px-6 py-5">
            <div className="mb-4">
              <div className="mb-1.5 flex justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-clay/70">
                <span>{effective >= threshold ? "Free insured shipping unlocked" : `${inr(threshold - effective)} to free shipping`}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-linen">
                <div className="h-full rounded-full bg-terra transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              {giftEnabled && giftProduct && !giftUnlocked && (
                <p className="mt-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-ochre" data-testid="freebie-progress">
                  <Gift className="h-3 w-3" /> {inr(giftThreshold - effective)} more for a free gift
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <input
                data-testid="cart-coupon-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Coupon code"
                className="h-10 flex-1 rounded-lg border border-linen bg-white px-3 font-mono text-sm uppercase tracking-widest outline-none focus:border-terra"
              />
              <button
                data-testid="cart-coupon-apply-button"
                onClick={applyCoupon}
                disabled={applying}
                className="h-10 rounded-lg bg-clay px-4 text-xs font-semibold uppercase tracking-widest text-cream transition-colors hover:bg-terra disabled:opacity-50"
              >
                Apply
              </button>
            </div>
            {coupon && (
              <p className="mt-2 flex items-center justify-between font-mono text-xs text-sage">
                <span>{coupon.code} — {coupon.discount_pct}% off applied</span>
                <button onClick={() => setCoupon(null)} className="text-clay/50 underline">remove</button>
              </p>
            )}

            <div className="mt-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-clay/70"><span>Subtotal</span><span className="font-mono">{inr(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-sage"><span>Coupon discount</span><span className="font-mono">−{inr(discount)}</span></div>}
              <div className="flex justify-between border-t border-linen pt-2 font-semibold text-espresso">
                <span>Total</span><span className="font-mono text-lg">{inr(effective)}</span>
              </div>
            </div>

            <button
              data-testid="cart-proceed-checkout-button"
              onClick={() => {
                setCartOpen(false);
                navigate("/checkout");
              }}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-terra py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-terra-dark active:scale-[0.98]"
            >
              Proceed to checkout <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
